import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';

import {
  filterStyles as styles,
  FilterField,
  FilterTextInput,
  FilterSelect,
  FilterMultiSelect,
  FilterNativeSelect,
  FilterRange,
} from 'app/src/elements/Filter/inlineFilters';
import {
  buyerOptions,
  saleOptions,
  sellerOptions,
  stringOptions,
} from 'app/src/components/Reports/filterOptions';
import { abbreviations as STATE_ABBREVIATIONS } from 'app/src/elements/states';
import TXN_TYPES from 'app/src/components/Accounting/Txns/types';

import { REGISTER_FILTER_OPTIONS } from './queries';

export const SCOPE_POSTING = 'posting';
export const SCOPE_NOT_VOID = 'not_void';
export const INCLUDE_VOID_FILTER = 'includeVoid';

const STATE_OPTIONS = stringOptions(STATE_ABBREVIATIONS);

const IndexFilters = ({ filters, setFilter, onClear }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { data } = useQuery(REGISTER_FILTER_OPTIONS);

  const options = useMemo(() => {
    const queryData = data || {};

    return {
      accounts: (queryData.accounts || []).map(account => (
        { value: account.id, label: account.fullName }
      )),
      sales: saleOptions(queryData),
      buyers: buyerOptions(queryData),
      sellers: sellerOptions(queryData),
      feeTypes: stringOptions(
        (queryData.feeTypes || []).map(feeType => feeType.name)
      ),
    };
  }, [data]);

  const range = (label, minKey, maxKey, type) => (
    <FilterRange
      label={label}
      type={type}
      wide={'date' === type}
      minValue={filters[minKey]}
      maxValue={filters[maxKey]}
      onChangeMin={value => setFilter(minKey, value)}
      onChangeMax={value => setFilter(maxKey, value)}
    />
  );

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        <FilterTextInput
          label="Search"
          value={filters.search}
          onChange={value => setFilter('search', value)}
        />
        <FilterMultiSelect
          label="Account"
          options={options.accounts}
          value={filters.accountId}
          onChange={value => setFilter('accountId', value)}
        />
        <FilterSelect
          label="Sale"
          options={options.sales}
          value={filters.saleId}
          onChange={value => setFilter('saleId', value)}
        />
        <FilterSelect
          label="Buyer"
          options={options.buyers}
          value={filters.buyerId}
          onChange={value => setFilter('buyerId', value)}
        />
        {range('Bank Date', 'bankDateFrom', 'bankDateTo', 'date')}
      </div>

      {showAdvanced &&
        <div style={styles.advancedGrid}>
          <FilterNativeSelect
            label="Txn Type"
            value={filters.txnType}
            onChange={value => setFilter('txnType', value)}
          >
            <option value="">Any</option>
            {TXN_TYPES.map(type => (
              <option key={`txn-type-${type}`} value={type}>{type}</option>
            ))}
          </FilterNativeSelect>
          <FilterSelect
            label="Fee Type"
            options={options.feeTypes}
            value={filters.feeType}
            onChange={value => setFilter('feeType', value)}
          />
          <FilterSelect
            label="Seller"
            options={options.sellers}
            value={filters.sellerId}
            onChange={value => setFilter('sellerId', value)}
          />
          <FilterMultiSelect
            label="Location State"
            options={STATE_OPTIONS}
            value={filters.locationState}
            onChange={value => setFilter('locationState', value)}
          />
          <FilterMultiSelect
            label="Buyer State"
            options={STATE_OPTIONS}
            value={filters.buyerState}
            onChange={value => setFilter('buyerState', value)}
          />
          {range('Sale Date', 'saleDateFrom', 'saleDateTo', 'date')}
          {range('Amount', 'amountMin', 'amountMax')}
          <FilterTextInput
            label="Memo"
            value={filters.memo}
            onChange={value => setFilter('memo', value)}
          />
          <FilterField label="Include Voided">
            <input
              type="checkbox"
              aria-label="Include Voided"
              checked={!!filters[INCLUDE_VOID_FILTER]}
              onChange={event =>
                setFilter(INCLUDE_VOID_FILTER, event.target.checked)}
            />
          </FilterField>
        </div>
      }

      <div style={styles.footer}>
        <button
          type="button"
          style={styles.linkButton}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Fewer search options' : 'More search options'}
        </button>
        <button
          type="button"
          style={styles.linkButton}
          onClick={onClear}
        >
          Clear filters
        </button>
      </div>
    </div>
  );
};

export default IndexFilters;
